"use client";

import TitleWithIcon from "@components/molecules/TitleWithIcon";
import Text from "@components/atoms/Text";
import Button from "@components/atoms/Button";
import styles from "./OnRoadPriceSection.module.scss";
import { formatCurrencyINR, formatPrice, maskAmount } from "@utils/numberFormatter";
import assetLoader from "@utils/internalImageLoder";
import CitySelectionModal from "../CitySelectionModal";
import { useEffect, useState } from "react";
import { addCaptureIntentIfNeeded, getLoginUrl } from "@utils/getLoginUrl";
import { PageType } from "@constants/pageTypes";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { useRouter } from "next/navigation";
import { useCity } from "@context/CityContext";
import { City } from "../CitySelectionModal/City.types";
import { defaultCity } from "@constants/cities";
import { normalizeCityName } from "@utils/urlNormalization";
import { LoginSdkConfig } from '@enums/LoginSdkConfig';
import { handleLoginFlow } from "@utils/loginFlowHandler";

export default function OnRoadPriceSection({
  slug,
  city,
  isLoggedIn,
  isSellable,
  pageType,
  carAvailability,
  modelData,
}: {
  slug: string;
  city: City;
  isLoggedIn?: boolean;
  isSellable: boolean;
  pageType?: PageType;
  carAvailability?: string;
  modelData: any;
}) {
  const router = useRouter();
  const { setCity: setContextCity, city: contextCity } = useCity();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cityName, setCityName] = useState(city.name || defaultCity.name);
  const [changed, setIsChanging] = useState(false);
  

  useEffect(() => {
    setContextCity({ ...city });
    setCityName(city?.name || defaultCity.name);
  }, [city]);

  useEffect(() => {
    if (contextCity.name !== city?.name && changed) {
      const citySlug = contextCity.city_slug
        ? contextCity.city_slug
        : normalizeCityName(contextCity?.name || '');
      router.push(`/cars/${slug}/price-in-${citySlug}/`);
      setIsChanging(false);
    }
  }, [contextCity]);


  const clickHandler = async () => {
    const mmvDetails = {
      make: modelData?.brand?.name || "",
      model: modelData?.model?.name || "",
      variant: "",
      color: "",
    };
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_login_to_save",
        section: "Summary",
        variant_name: "",
        car_availability: carAvailability || "",
        vehicle_make: modelData?.brand?.name || "",
        vehicle_model: modelData?.model?.name || "",
        city_name: cityName,
      },
    });
    if (typeof window !== "undefined") {
      sessionStorage.setItem("loginType", "Reveal price");
      localStorage.setItem(
        "MMV",
        JSON.stringify({
          make: mmvDetails?.make || "",
          model: mmvDetails?.model || "",
          variant: mmvDetails?.variant || "",
          color: mmvDetails?.color || "",
        })
      );
    }
    const loginUrl = getLoginUrl(
      addCaptureIntentIfNeeded(
        window.location.href,
        mmvDetails,
        "Reveal price",
        pageType
      )
    );
    await handleLoginFlow(loginUrl, LoginSdkConfig.AD_PRICE);
  };

  const handleCitySelection = (city: City) => {
    const citySlug = city.city_slug
      ? city.city_slug
      : normalizeCityName(city?.name || '');
    setIsChanging(true);
    router.push(`/cars/${slug}/price-in-${citySlug}/`);
  };

  return (
    <div className={styles.wrapper}>
      <CitySelectionModal
        selectedCity={city}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        handleCitySelection={handleCitySelection}
      />
      <div className={styles.heading} onClick={() => setIsModalOpen(true)}>
        <TitleWithIcon
          title={`${modelData?.brand.name} ${modelData?.model.name} On-Road Price in ${cityName}`}
          iconSrc={`${assetLoader()}/icons/Edit_pencil_change.png`}
          alt="Edit icon"
        />
      </div>

      <div className={styles.priceContainer}>
        <Text level="p" className={styles.price}>
          {`${formatPrice(
            modelData?.minPrice ?? 0
          )} – ${formatPrice(modelData?.maxPrice ?? 0)}`}
        </Text>
        {isSellable && (
          <Text level="p" className={styles.savings}>
            Save up to{" "}
            {isLoggedIn
              ? formatCurrencyINR(modelData?.minAckodriveSavings ?? 0)
              : maskAmount(modelData?.minAckodriveSavings ?? 0)}
          </Text>
        )}
      </div>
      {isSellable && (
        <>
          {!isLoggedIn && (
            <Button
              className={styles.button}
              ariaLabel="Check ACKO Drive price"
              onClick={clickHandler}
            >
              {`Check ACKO Drive price`}
            </Button>
          )}
        </>
      )}
      {!isSellable && (
        <Text level="p" className={styles.unavailable}>
          {`**All the details are based on speculation.`}
        </Text>
      )}
    </div>
  );
}
